/**
 * Klaviyo newsletter bridge — progressive enhancement for the theme's native
 * newsletter forms (snippets/newsletter-form.liquid).
 *
 * No JS / Klaviyo disabled  → the {% form 'customer' %} posts to Shopify as before.
 * JS + Klaviyo enabled      → submit is intercepted and the email is subscribed to
 *                             the configured Klaviyo list via the public Client API.
 *
 * Config comes from window.drlhKlaviyo (injected by layout/theme.liquid). Only the
 * public Company ID + List ID are used — never a private key. Status copy is read
 * from data-* attributes the snippet renders through Liquid `| t`, so EN/RU is
 * resolved server-side and this file stays locale-agnostic.
 */
(function () {
  'use strict';

  var cfg = window.drlhKlaviyo;
  if (!cfg || !cfg.companyId) return;

  // Klaviyo Client API revision. Bump deliberately when adopting a newer schema.
  var KLAVIYO_REVISION = '2024-10-15';
  var ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.companyId);
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Record WHICH guide was requested, as a Klaviyo event.
   *
   * Events accumulate; profile properties overwrite. "Do not offer a guide they
   * already claimed" needs the full history, so a flow filter of
   * "has done Requested Guide where guide = skin-capillary at least once ever"
   * is the only shape that stays correct past the second signup.
   *
   * Fire-and-forget: a failure here must never block delivery of the PDF the
   * visitor just paid an email for.
   */
  function trackEvent(metricName, email, props) {
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(cfg.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify({
          data: {
            type: 'event',
            attributes: {
              properties: props,
              metric: { data: { type: 'metric', attributes: { name: metricName } } },
              profile: { data: { type: 'profile', attributes: { email: email } } }
            }
          }
        })
      }).catch(function () {});
    } catch (e) { /* never block the unlock */ }
  }

  function trackGuideRequest(email, slug) {
    trackEvent('Requested Guide', email, { guide: slug });
  }

  function setStatus(form, type, message) {
    var node = form.querySelector('[data-klaviyo-status]');
    if (!node) return;
    node.textContent = message || '';
    node.setAttribute('data-state', type); // 'idle' | 'loading' | 'success' | 'error'
    node.hidden = !message;
  }

  // Redirect countdown. REDIRECT_MS drives both the ring animation and the
  // navigation, so they cannot drift apart. Identical on phones and desktop.
  var REDIRECT_MS = 3000;

  function startRedirect(el, url) {
    if (el) {
      el.classList.add('drlh-signup-note');
      var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!still && !el.querySelector('.drlh-signup-note__ring')) {
        var ring = document.createElement('span');
        ring.className = 'drlh-signup-note__ring';
        ring.setAttribute('aria-hidden', 'true');
        ring.style.setProperty('--drlh-redirect-ms', REDIRECT_MS + 'ms');
        el.insertBefore(ring, el.firstChild);
      }
    }
    setTimeout(function () { window.location.href = url; }, REDIRECT_MS);
  }

  /**
   * The profile half of a signup, shared by both endpoints below.
   *
   * Split out when the guide gate gained an opt-in checkbox: the same properties
   * have to reach Klaviyo whether or not the visitor consented to marketing,
   * because they are what delivers the guide, not what markets to them.
   */
  function buildProfileAttrs(email, source, guide) {
    // Site locale (en / ru) → drlh_lang, so Klaviyo can split the welcome flow
    // and lead magnet by language. Read from <html lang> (request.locale.iso_code).
    var lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
    var profileAttrs = { email: email };
    var props = {};
    if (lang) props.drlh_lang = lang;

    // Which lead magnet this signup came from.
    //
    // custom_source below already carries "guide:endurance", but that lives on
    // the subscription event, so a flow cannot filter on it after the trigger.
    // Without this, the welcome sequence hands everyone the same guide:
    // subscribe on the Endurance page, receive the Vascular Age guide.
    //
    // drlh_last_guide is a PROFILE property because it answers "which guide do
    // I deliver right now", and the newest value is the only correct one.
    //
    // The set of guides someone has claimed is NOT a profile property. Writing
    // it here would overwrite, not append (the client API has no read), so it
    // would only ever hold the most recent slug and "don't offer what they
    // already have" logic would silently be wrong. That belongs in an EVENT,
    // which accumulates by nature. See trackGuideRequest below.
    if (source && source.indexOf('guide:') === 0) props.drlh_last_guide = source.slice(6);

    // The title and file the visitor actually asked for, emitted by
    // sections/drlh-pdf-guide.liquid in the right locale. welcome-1 renders
    // these, so one email delivers the correct guide without the flow
    // branching per guide (which would not survive a twentieth guide).
    if (guide && guide.title) props.drlh_guide_title = guide.title;
    if (guide && guide.pdf) props.drlh_guide_pdf = guide.pdf;

    if (Object.keys(props).length) profileAttrs.properties = props;
    return profileAttrs;
  }

  function subscribe(profileAttrs, listId, source) {
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: source || 'Newsletter',
          profile: { data: { type: 'profile', attributes: profileAttrs } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    return fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
      body: JSON.stringify(body)
    });
  }

  function handleSubmit(form, node, event) {
    var listId = node.getAttribute('data-klaviyo-list') || cfg.listId;
    if (!listId) return; // No list configured — let Shopify handle it natively.

    var input = form.querySelector('input[type="email"]');
    var email = input ? input.value.trim() : '';
    if (!EMAIL_RE.test(email)) {
      event.preventDefault();
      setStatus(form, 'error', node.getAttribute('data-msg-invalid') || '');
      if (input) input.focus();
      return;
    }

    event.preventDefault();
    var button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;
    setStatus(form, 'loading', node.getAttribute('data-msg-loading') || '');

    var source = node.getAttribute('data-klaviyo-context') || 'Newsletter';
    var gateEl = form.closest ? form.closest('[data-guide-gate]') : null;
    var guide = gateEl
      ? { title: gateEl.getAttribute('data-guide-title'), pdf: gateEl.getAttribute('data-guide-pdf') }
      : null;

    // No opt-in checkbox, by decision (2026-08-08, KB decision log): the
    // form carries a plain-language line saying the signup includes the
    // newsletter, so every submit subscribes. The welcome flow is
    // entry-filtered in Klaviyo, so an existing subscriber never re-enters it.
    var profileAttrs = buildProfileAttrs(email, source, guide);
    subscribe(profileAttrs, listId, source)
      .then(function (res) {
        if (res.status === 202 || res.ok) {
          setStatus(form, 'success', node.getAttribute('data-msg-success') || '');
          if (input) input.value = '';
          // Same handoff as the email-CTA: stash the email (session-scoped) so the
          // thank-you quiz can attach its preference answers to this Klaviyo
          // profile, then redirect there when the form opts in via
          // data-klaviyo-redirect. Cleared by drlh-thank-you.liquid after use.
          try { sessionStorage.setItem('drlh_email', email); } catch (e) {}
          if (source && source.indexOf('guide:') === 0) trackGuideRequest(email, source.slice(6));
          var redirect = node.getAttribute('data-klaviyo-redirect');
          if (redirect) {
            // Same treatment as the email-CTA path (assets/klaviyo.js): the
            // confirmation becomes a padded note with a ring that fills over the
            // wait, so the page does not change under the reader unannounced.
            startRedirect(form.querySelector('[data-klaviyo-status]'), redirect);
          }
        } else {
          setStatus(form, 'error', node.getAttribute('data-msg-error') || '');
        }
      })
      .catch(function () {
        setStatus(form, 'error', node.getAttribute('data-msg-error') || '');
      })
      .finally(function () {
        if (button) button.disabled = false;
      });
  }

  function init() {
    var nodes = document.querySelectorAll('[data-klaviyo-config]');
    for (var i = 0; i < nodes.length; i++) {
      (function (node) {
        var form = node.closest('form');
        if (!form) return;
        form.addEventListener('submit', function (event) {
          handleSubmit(form, node, event);
        });
      })(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
